import { useState, useRef, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { createConsumer } from '@rails/actioncable';
import axios from 'axios';
import { v4 as uuid } from 'uuid';

import { post } from 'helpers/api';
import { useSubscription } from 'helpers/action_cable/use_subscription';

import type { Consumer, Subscription } from 'helpers/action_cable/types';

interface BatchPayload {
  status: string;
  results?: BatchDocResult[];
  error?: { message?: string };
  ingest_review_id?: number;
}

const ACCEPTED_FILE_TYPES = {
  'application/pdf': 'PDF',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Microsoft Word',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Microsoft Excel',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'Powerpoint'
};
const ACCEPTED_MIME_TYPES = Object.keys(ACCEPTED_FILE_TYPES);
export const ACCEPTED_TYPE_NAMES = Object.values(ACCEPTED_FILE_TYPES);

export const MAX_FILES = 10;

export type DocUploadStatus = 'idle' | 'uploading' | 'upload_error' | 'invalid';

export type DocInvalidReason = 'invalid_type' | 'limit_exceeded';

export interface PendingDoc {
  id: string;
  file: File;
  status: DocUploadStatus;
  uploadToken: string | null; // set after the file is uploaded to /property_analyze_doc/upload
  error: string | null;
  invalidReason?: DocInvalidReason;
}

export type BatchStatus = 'idle' | 'uploading' | 'processing' | 'complete' | 'error';

export interface BatchDocResultData {
  propertyFormData: Record<string, unknown>;
  populatedFieldNames: string[];
  inferredFields: string[];
  fieldsValid: boolean;
  llmData?: Record<string, unknown>;
  uploadMetadata?: {
    filename?: string;
    uploadId?: number;
    aiModel?: string;
  };
}

export interface BatchDocResult {
  status: 'success' | 'error';
  upload_id: number;
  filename?: string;
  data?: BatchDocResultData;
  error?: { code: string; message: string };
}

export interface PendingIngestReview {
  id: number;
  sessionUuid: string;
}

export interface UseMultiDocUploadReturn {
  docs: PendingDoc[];
  batchStatus: BatchStatus;
  batchError: string | null;
  fileDropError: string | null;
  isDragActive: boolean;
  canAddMore: boolean;
  getRootProps: () => object;
  getInputProps: () => object;
  removeDoc: (id: string) => void;
  uploadFiles: (docs: PendingDoc[]) => Promise<string[]>;
  processFiles: (docs: PendingDoc[]) => Promise<void>;
  reset: () => void;
}

export function useMultiDocUpload(
  onAllComplete?: (results: BatchDocResult[], ingestReviewId: number | null) => void,
  onProcessingStarted?: (ingestReviewId: number) => void
): UseMultiDocUploadReturn {
  const [docs, setDocs] = useState<PendingDoc[]>([]);
  const [batchStatus, setBatchStatus] = useState<BatchStatus>('idle');
  const [batchError, setBatchError] = useState<string | null>(null);
  const [fileDropError, setFileDropError] = useState<string | null>(null);

  const { subscribe, unsubscribe } = useSubscription();
  const onAllCompleteRef = useRef(onAllComplete);
  onAllCompleteRef.current = onAllComplete;
  const onProcessingStartedRef = useRef(onProcessingStarted);
  onProcessingStartedRef.current = onProcessingStarted;

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFileDropError(null);
    setDocs((prev) => {
      const validCount = prev.filter(d => d.status !== 'invalid').length;
      const newDocs = acceptedFiles.map((file, idx): PendingDoc => {
        const overLimit = validCount + idx >= MAX_FILES;
        return overLimit
          ? {
            id: uuid(),
            file,
            status: 'invalid',
            uploadToken: null,
            error: null,
            invalidReason: 'limit_exceeded',
          }
          : {
            id: uuid(),
            file,
            status: 'idle',
            uploadToken: null,
            error: null,
          };
      });
      return [...prev, ...newDocs];
    });
  }, []);

  const onDropRejected = useCallback((rejectedFiles: File[]) => {
    setDocs(prev => [
      ...prev,
      ...rejectedFiles.map((file): PendingDoc => ({
        id: uuid(),
        file,
        status: 'invalid',
        uploadToken: null,
        error: null,
        invalidReason: 'invalid_type',
      })),
    ]);
  }, []);

  const removeDoc = useCallback((id: string) => {
    setDocs((prev) => {
      const filtered = prev.filter(d => d.id !== id);
      let validCount = filtered.filter(d => d.status !== 'invalid').length;
      return filtered.map((d): PendingDoc => {
        if (d.invalidReason === 'limit_exceeded' && validCount < MAX_FILES) {
          validCount += 1;
          return { ...d, status: 'idle', invalidReason: undefined };
        }
        return d;
      });
    });
  }, []);

  // Uploads a single file and returns its upload_token, or null on failure.
  const uploadFile = async (doc: PendingDoc): Promise<string | null> => {
    setDocs(prev => prev.map(d => (d.id === doc.id ? { ...d, status: 'uploading' } : d)));
    try {
      const formData = new FormData();
      formData.append('file', doc.file);
      const { data } = await axios.post<{ upload_token: string }>(
        '/property_analyze_doc/upload',
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setDocs(prev => prev.map(d => (
        d.id === doc.id ? { ...d, status: 'idle', uploadToken: data.upload_token, error: null } : d
      )));
      return data.upload_token;
    } catch (err: unknown) {
      const message = axios.isAxiosError(err)
        ? ((err.response?.data as { message?: string })?.message) ?? 'Upload failed'
        : 'Upload failed';
      setDocs(prev => prev.map(d => (d.id === doc.id ? { ...d, status: 'upload_error', error: message } : d)));
      return null;
    }
  };

  // Unlike processFiles, this retries a file whose earlier upload failed.
  const uploadFiles = async (docs: PendingDoc[]): Promise<string[]> => {
    const uploadResults = await Promise.all(docs.filter(d => d.status !== 'invalid').map(uploadFile));
    return uploadResults.filter((token): token is string => token !== null);
  };

  const processFiles = async (docs: PendingDoc[]) => {
    const validDocs = docs.filter(d => d.status !== 'upload_error' && d.status !== 'invalid');
    if (validDocs.length === 0) return;

    setBatchStatus('uploading');
    setBatchError(null);

    // Step 1 — upload all files in parallel, collect upload_tokens
    const uploadTokens = await uploadFiles(validDocs);

    if (uploadTokens.length === 0) {
      setBatchStatus('error');
      setBatchError('All file uploads failed. Please try again.');
      return;
    }

    // Step 2 — open cable subscription, then call process_docs once connected
    const sessionUUID = uuid();
    setBatchStatus('processing');

    subscribe(
      { channel: 'Projects::ListingDocUploadChannel', upload_uuid: sessionUUID },
      {
        connected: async () => {
          try {
            const data = await post('/property_analyze_doc/process', {
              upload_tokens: uploadTokens,
              session_uuid: sessionUUID,
              filenames: validDocs.map(d => d.file.name),
            });
            onProcessingStartedRef.current?.(data.ingestReviewId);
          } catch {
            setBatchStatus('error');
            setBatchError('Failed to start processing. Please try again.');
            unsubscribe();
          }
        },
        received: (payload: BatchPayload) => {
          unsubscribe();
          if (payload.status === 'error') {
            setBatchStatus('error');
            setBatchError(payload.error?.message ?? 'Processing failed');
          } else {
            setBatchStatus('complete');
            onAllCompleteRef.current?.(payload.results ?? [], payload.ingest_review_id ?? null);
          }
        },
      }
    );
  };

  const reset = useCallback(() => {
    unsubscribe();
    setDocs([]);
    setBatchStatus('idle');
    setBatchError(null);
    setFileDropError(null);
  }, [unsubscribe]);

  const validDocCount = docs.filter(d => d.status !== 'invalid').length;
  const canAddMore = validDocCount < MAX_FILES && batchStatus === 'idle';

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected,
    disabled: !canAddMore,
    accept: ACCEPTED_MIME_TYPES,
    multiple: true,
  });

  return {
    docs,
    batchStatus,
    batchError,
    fileDropError,
    isDragActive: isDragActive && canAddMore,
    canAddMore,
    getRootProps,
    getInputProps,
    removeDoc,
    uploadFiles,
    processFiles,
    reset,
  };
}

// Re-subscribes to an existing cable session for a pending IngestReview (e.g. after page reload).
// Returns a cleanup function to unsubscribe.
export function resubscribeToIngestReview(
  sessionUuid: string,
  onComplete: (results: BatchDocResult[], ingestReviewId: number | null) => void,
  onError: (message: string) => void
): () => void {
  const consumer = createConsumer() as Consumer;
  let sub: Subscription | null = null;

  sub = consumer.subscriptions.create(
    { channel: 'Projects::ListingDocUploadChannel', upload_uuid: sessionUuid },
    {
      received: (payload: BatchPayload) => {
        sub?.unsubscribe();
        consumer.disconnect();
        if (payload.status === 'error') {
          onError(payload.error?.message ?? 'Processing failed');
        } else {
          onComplete(payload.results ?? [], payload.ingest_review_id ?? null);
        }
      },
    }
  );

  return () => {
    sub?.unsubscribe();
    consumer.disconnect();
  };
}
